import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Scale, Home as HomeIcon, Landmark, Briefcase } from 'lucide-react';
import api from '../lib/api';

const iconFor = (area) => {
  const a = (area || '').toLowerCase();
  if (a.includes('property') || a.includes('conveyanc')) return HomeIcon;
  if (a.includes('dispute')) return Scale;
  if (a.includes('housing')) return Landmark;
  if (a.includes('commercial')) return Building2;
  return Briefcase;
};

export default function CaseStudies() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/case-studies'); setItems(data); }
      finally { setLoading(false); }
    })();
  }, []);

  const areas = ['All', ...Array.from(new Set(items.map((c) => c.service_area).filter(Boolean)))];
  const filtered = filter === 'All' ? items : items.filter((c) => c.service_area === filter);

  return (
    <div className="pt-24">
      <section className="bg-forest-950 py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <p className="text-sage-400 tracking-[0.3em] text-xs uppercase mb-5 reveal">Client work</p>
          <h1 className="font-serif text-cream-50 text-5xl md:text-7xl leading-tight reveal">Case Studies</h1>
          <p className="mt-6 text-cream-100/75 text-lg max-w-2xl reveal">A selection of matters we have delivered for our clients. Confidentiality is preserved; identifying details have been anonymised.</p>
        </div>
      </section>

      <section className="bg-forest-900 py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {loading ? <p className="text-cream-100/60">Loading case studies...</p> : (
            <>
              <div className="flex flex-wrap gap-2 mb-10 border-b border-forest-800 pb-4">
                {areas.map((a) => (
                  <button key={a} onClick={() => setFilter(a)} className={`text-sm px-4 py-2 transition-colors ${filter === a ? 'bg-sage-500 text-forest-900' : 'text-cream-100/70 hover:text-cream-50 border border-forest-800 hover:border-sage-500/60'}`}>{a}</button>
                ))}
              </div>

              {filtered.length === 0 ? <p className="text-cream-100/60">No case studies match this filter.</p> : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filtered.map((c, i) => {
                    const Icon = iconFor(c.service_area);
                    return (
                      <Link key={c.id} to={`/case-studies/${c.slug}`} className="group bg-forest-950 border border-forest-800 hover:border-sage-500/50 transition-colors overflow-hidden animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                        {c.cover && <div className="aspect-[16/9] overflow-hidden"><img src={c.cover} alt={c.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" /></div>}
                        <div className="p-6">
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full bg-sage-500/15 flex items-center justify-center"><Icon className="h-4 w-4 text-sage-400" /></div>
                            <p className="text-sage-400 text-[11px] uppercase tracking-[0.25em]">{c.service_area}</p>
                          </div>
                          <h3 className="mt-4 font-serif text-cream-50 text-2xl leading-snug group-hover:text-sage-400 transition-colors">{c.title}</h3>
                          {c.client_type && <p className="mt-1 text-cream-100/50 text-xs italic">{c.client_type}</p>}
                          <p className="mt-3 text-cream-100/75 text-sm leading-relaxed">{c.summary}</p>
                          <span className="mt-5 inline-flex items-center gap-2 text-sage-400 text-xs tracking-widest uppercase">Read case study <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <section className="bg-forest-800 py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <h3 className="font-serif text-cream-50 text-3xl md:text-4xl">Have a matter of your own?</h3>
          <p className="mt-4 text-cream-100/75">We would be pleased to discuss how we can help.</p>
          <Link to="/contact" className="mt-8 inline-flex items-center gap-3 bg-sage-500 text-forest-900 px-8 py-4 text-sm font-medium tracking-wide hover:bg-sage-600 transition-colors">Contact Us <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </div>
  );
}
